/* ═══════════════════════════════════════════════════════════════
   REVIEW — the Revert lane's state (Tweaks 36), shared by the master's
   Spec Studio and the project spec's editor.

   start(theirs, target) compares the live document (OURS) with an older
   version (THEIRS) and paints every change with the same Review plugin the
   Sync road uses (not inverted: accept = take the old words back). The
   decisions live here only — nothing is written until Apply revert, which
   lands the accepted mix on the live editor as ONE transaction (one undo
   step, a normal edit of the draft). Cancel paints nothing and writes
   nothing.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import type { Editor } from '@tiptap/react';
import type { Change, Decision } from '../../shared/sync';
import type { ContentBlock } from '../../shared/types';
import { docToBlocks } from '../designer/schema';
import { replaceDocWithBlocks } from './applyBlocks';
import { useChangeWalker } from './useReview';
import { useReviewDecorations } from './useReviewDecorations';
import type { ReviewPluginInput } from './reviewPlugin';
import { applyRevert, decideAll, revertChanges, type RevertResult } from './revertReview';

const NO_CHANGES: Change[] = [];

export interface RevertSession {
  /** What the lane reverts to, in words ("version 3"). */
  target: string;
  /** The History line's short name ("v3"). */
  short: string;
  changes: Change[];
}

export function revertLaneTitle(target: string): string {
  return `Reverting to ${target} — accept the changes you want back`;
}

export function useRevertReview(editor: Editor | null) {
  const [session, setSession] = React.useState<RevertSession | null>(null);
  const [decisions, setDecisions] = React.useState<Record<string, Decision>>({});
  const items = session?.changes ?? NO_CHANGES;
  const walker = useChangeWalker(items);
  const currentId = walker.current?.id ?? null;
  const input = React.useMemo<ReviewPluginInput | null>(
    () => (session ? { changes: items, decisions, currentId, source: session.target } : null),
    [session, items, decisions, currentId]
  );
  useReviewDecorations(editor, input);

  const editorRef = React.useRef(editor);
  editorRef.current = editor;

  /** Open the lane. 'nothing' = the document already matches. */
  const start = React.useCallback(
    (theirs: ContentBlock[], target: string, short: string): 'opened' | 'nothing' | 'not-ready' => {
      const ed = editorRef.current;
      if (!ed || ed.isDestroyed) return 'not-ready';
      const changes = revertChanges(docToBlocks(ed.getJSON()), theirs || []);
      if (!changes.length) return 'nothing';
      setDecisions({});
      setSession({ target, short, changes });
      return 'opened';
    },
    []
  );

  const decide = React.useCallback((id: string, d: Decision) => {
    setDecisions((prev) => (prev[id] === d ? prev : { ...prev, [id]: d }));
  }, []);
  const acceptAll = React.useCallback(() => setDecisions(decideAll(items, 'accept')), [items]);
  const rejectAll = React.useCallback(() => setDecisions(decideAll(items, 'reject')), [items]);
  const cancel = React.useCallback(() => {
    setSession(null);
    setDecisions({});
  }, []);

  /** Land the accepted mix on the live document (nothing when none were
   *  accepted) and close the lane. null = the editor is gone. */
  const apply = React.useCallback((): (RevertResult & { short: string }) | null => {
    const ed = editorRef.current;
    if (!session || !ed || ed.isDestroyed) return null;
    const res = applyRevert(docToBlocks(ed.getJSON()), session.changes, decisions);
    if (res.accepted > 0) {
      const tr = replaceDocWithBlocks(ed.state, res.blocks);
      if (tr) ed.view.dispatch(tr);
    }
    setSession(null);
    setDecisions({});
    return { ...res, short: session.short };
  }, [session, decisions]);

  return {
    session,
    active: !!session,
    items,
    decisions,
    walker,
    currentId,
    remaining: items.filter((c) => !decisions[c.id]).length,
    start,
    decide,
    acceptAll,
    rejectAll,
    cancel,
    apply,
  };
}

export type RevertReviewApi = ReturnType<typeof useRevertReview>;
